import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { getCategoryList } from '../../../api/services/systemService';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isTokenExpired } from '../../../utils';

const SectionContainer = styled.section`
  margin: 0 auto;
  text-align: center;
  width: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Desktop/Tablet Grid Container
const CategoryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  justify-content: space-between;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
    width: 95%;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

// Mobile Slider Container
const MobileSlider = styled.div`
  display: none;
  position: relative;
  overflow: hidden;
  width: 100%;

  @media (max-width: 480px) {
    display: block;
    width: 100%;
    padding: 0 5px 40px 5px;
  }
`;

const SliderTrack = styled.div`
  display: flex;
  transition: transform 0.3s ease-in-out;
  transform: translateX(${(props) => props.currentSlide * -100}%);
`;

const CategoryCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: ${(props) => props.theme.colors.primaryText};
  box-sizing: border-box;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    width: 50%;
    flex-shrink: 0;
    align-items: center;
    box-sizing: border-box;
    padding: 10px;
  }
`;

const CategoryImage = styled.img`
  background-color: #f8f9fa;
  width: 100%;
  height: 220px;
  margin-bottom: 12px;
  max-width: 100%;
  object-fit: cover;
  min-height: 220px;

  @media (max-width: 768px) {
    height: 140px;
    min-height: 140px;
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 160px;
    min-height: 160px;
    margin-bottom: 8px;
    max-width: 100%;
  }
`;

const CategoryTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #333;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const DotsContainer = styled.div`
  display: none;
  position: absolute;
  width: 100%;
  bottom: 10px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  z-index: 10;

  @media (max-width: 480px) {
    display: flex;
    bottom: 15px;
    gap: 6px;
  }
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ccc;
  cursor: pointer;
  transition: background-color 0.3s ease;

  ${(props) =>
    props.active &&
    `
      background-color: #000;
      transform: scale(1.2);
    `}

  @media (max-width: 480px) {
    width: 6px;
    height: 6px;
  }
`;

export default function CategoryView() {
  const userInfo = useSelector((state) => state.userInfo);
  const navigate = useNavigate();

  const [categoryList, setCategoryList] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    getDbCategoryList();
  }, []);

  const getDbCategoryList = async () => {
    const result = await getCategoryList(userInfo.brandCode);
    setCategoryList(result.categoryList);
  };

  const handleCategoryClick = (categoryUrl) => {
    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      navigate('/login');
    } else {
      // Save flag to scroll back to category section when returning
      sessionStorage.setItem('scrollToCategoryView', 'true');
      // Extract the path after the # from the URL
      const path = categoryUrl.split('#')[1] || categoryUrl;
      navigate(path);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) =>
      prev === categoryList.length - 1 ? prev : prev + 1,
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? prev : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  return (
    <SectionContainer id='category-section'>
      {/* Desktop Grid */}
      <CategoryGrid>
        {categoryList.map((category) => (
          <CategoryCard
            key={category.title}
            onClick={() => handleCategoryClick(category.url)}
          >
            <CategoryImage
              src={`${process.env.REACT_APP_FILE_UPLOAD_URL}${category.filepath1}`}
              alt={category.title}
            />
            <CategoryTitle>{category.title}</CategoryTitle>
          </CategoryCard>
        ))}
      </CategoryGrid>

      {/* Mobile Slider */}
      <MobileSlider
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <SliderTrack currentSlide={currentSlide}>
          {categoryList.map((category) => (
            <CategoryCard
              key={category.title}
              onClick={() => handleCategoryClick(category.url)}
            >
              <CategoryImage
                src={`${process.env.REACT_APP_FILE_UPLOAD_URL}${category.filepath1}`}
                alt={category.title}
              />
              <CategoryTitle>{category.title}</CategoryTitle>
            </CategoryCard>
          ))}
        </SliderTrack>
        <DotsContainer>
          {[0, 1].map((idx) => (
            <Dot
              key={idx}
              active={Math.floor(currentSlide / 2) === idx}
              onClick={() => goToSlide(idx * 2)}
            />
          ))}
        </DotsContainer>
      </MobileSlider>
    </SectionContainer>
  );
}
